import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { GoogleTagManager } from "./components/GoogleTagManager";
import Layout from "./components/layout/Layout";
import Home from "./pages/Home";
import PageGuard from "./components/PageGuard";

// Lazy-loaded routes to reduce initial JS bundle (improves LCP/FCP).
// Home is kept eager because it is the main landing page.
const JobsPage = lazy(() => import("./pages/JobsPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const EspacePage = lazy(() => import("./pages/EspacePage"));
const MetiersPage = lazy(() => import("./pages/MetiersPage"));
const MetierDetailPage = lazy(() => import("./pages/MetierDetailPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticleDynamicPage = lazy(() => import("./pages/BlogArticleDynamicPage"));
const InscriptionMedecinPage = lazy(() => import("./pages/InscriptionMedecinPage"));
const MentionsLegalesPage = lazy(() => import("./pages/MentionsLegalesPage"));
const ConfidentialitePage = lazy(() => import("./pages/ConfidentialitePage"));
const PlanDuSitePage = lazy(() => import("./pages/PlanDuSitePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminDocumentsPage = lazy(() => import("./pages/AdminDocumentsPage"));
const AdminMetiersPage = lazy(() => import("./pages/AdminMetiersPage"));
const AdminOffresPage = lazy(() => import("./pages/AdminOffresPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminMessagesPage = lazy(() => import("./pages/AdminMessagesPage"));
const AdminBlogPage = lazy(() => import("./pages/AdminBlogPage"));
const FixMetaDescriptionsPage = lazy(() => import("./pages/FixMetaDescriptionsPage"));
const PageUnavailablePage = lazy(() => import("./pages/PageUnavailablePage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <GoogleTagManager />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            {/* Routes sans layout */}
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/documents" element={<AdminDocumentsPage />} />
            <Route path="/admin/metiers" element={<AdminMetiersPage />} />
            <Route path="/admin/offres" element={<AdminOffresPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />

            <Route path="/admin/messages" element={<AdminMessagesPage />} />
            <Route path="/admin/blog" element={<AdminBlogPage />} />
            <Route path="/admin/fix-meta" element={<FixMetaDescriptionsPage />} />
            <Route path="/page-indisponible" element={<PageUnavailablePage />} />

            {/* Routes avec layout et protection */}
            <Route path="/" element={<Layout><PageGuard><Home /></PageGuard></Layout>} />
            <Route path="/offres" element={<Layout><PageGuard><JobsPage /></PageGuard></Layout>} />
            <Route path="/offres/:id" element={<Layout><PageGuard><JobDetailPage /></PageGuard></Layout>} />
            <Route path="/about" element={<Layout><PageGuard><AboutPage /></PageGuard></Layout>} />
            <Route path="/contact" element={<Layout><PageGuard><ContactPage /></PageGuard></Layout>} />
            <Route path="/espace-medecin" element={<Layout><PageGuard><EspacePage /></PageGuard></Layout>} />
            <Route path="/metiers" element={<Layout><PageGuard><MetiersPage /></PageGuard></Layout>} />
            <Route path="/metiers/:slug" element={<Layout><PageGuard><MetierDetailPage /></PageGuard></Layout>} />
            <Route path="/faq" element={<Layout><PageGuard><FAQPage /></PageGuard></Layout>} />
            <Route path="/blog" element={<Layout><PageGuard><BlogPage /></PageGuard></Layout>} />
            <Route path="/blog/:slug" element={<Layout><PageGuard><BlogArticleDynamicPage /></PageGuard></Layout>} />
            <Route path="/inscription-medecin" element={<Layout><PageGuard><InscriptionMedecinPage /></PageGuard></Layout>} />
            <Route path="/mentions-legales" element={<Layout><PageGuard><MentionsLegalesPage /></PageGuard></Layout>} />
            <Route path="/confidentialite" element={<Layout><PageGuard><ConfidentialitePage /></PageGuard></Layout>} />
            <Route path="/plan-du-site" element={<Layout><PageGuard><PlanDuSitePage /></PageGuard></Layout>} />

            {/* 404 */}
            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
